import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { db, auth } from '../firebase';
import { AuthContextType, User, ShippingAddress } from '../types';
import { AuthLoadingScreen } from '../components/AuthLoadingScreen';

import { ShieldAlert, LogOut } from 'lucide-react';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CONSULTANT_NUMBER = '+911122334455';

const getDeviceFingerprint = () => {
  const info = {
    ua: navigator.userAgent,
    lang: navigator.language,
    depth: screen.colorDepth,
    res: `${screen.width}x${screen.height}`,
    tz: new Date().getTimezoneOffset(),
    mem: (navigator as any).deviceMemory || 'unknown',
    plat: navigator.platform
  };
  return btoa(JSON.stringify(info)).replace(/[/+=]/g, '').substring(0, 32);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthInitializing(false);
          return;
        } catch (e) {
          console.error("Failed to parse stored user:", e);
          localStorage.removeItem('user');
        }
      }

      const storedDeviceId = localStorage.getItem('devine_device_id');
      if (storedDeviceId) {
        try {
          const deviceRef = doc(db, "devices", storedDeviceId);
          const deviceSnap = await getDoc(deviceRef);
          
          if (deviceSnap.exists()) {
            const { email, mobile, uid } = deviceSnap.data();
            // FIX: Universal Identity Resolution
            const identifier = email || uid || mobile; 
            if (identifier) {
              const userRef = doc(db, "users", identifier);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                const userObj = {
                  ...userData,
                  isConsultant: userData.mobile === CONSULTANT_NUMBER || userData.isConsultant === true
                } as User;
                setUser(userObj);
                localStorage.setItem('user', JSON.stringify(userObj));
              }
            }
          }
        } catch (error) {
          console.error("Auto-login failed:", error);
        }
      }
      setIsAuthInitializing(false);
    };

    initializeAuth();
  }, []);

  const phoneLogin = async (userData: any) => {
    setUser(userData);
    setIsLoginOpen(false);
    
    const deviceId = getDeviceFingerprint();
    localStorage.setItem('devine_device_id', deviceId);
    try {
      await setDoc(doc(db, "devices", deviceId), {
        uid: userData.uid || null,
        email: userData.email || null,
        mobile: userData.mobile || null, 
        lastLogin: new Date().toISOString() 
      });
    } catch (e) {
      console.error("Failed to save device info:", e);
    }
  };

  const updateUserProfile = async (mobile: string, address?: ShippingAddress) => {
    if (!user) return;

    const isConsultant = mobile === CONSULTANT_NUMBER;

    try {
      // FIX: Stop forcing updates to `user.mobile`. Use universal docId!
      const docId = user.email || user.uid || user.mobile;
      const userRef = doc(db, "users", docId);
      
      const updateData: any = {
        isConsultant: isConsultant
      };
      
      if (address) {
        updateData.shippingAddress = address;
      }

      await setDoc(userRef, updateData, { merge: true });

      setUser(prev => prev ? { 
        ...prev, 
        isConsultant,
        shippingAddress: address || prev.shippingAddress 
      } : null);
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('devine_device_id');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, phoneLogin, updateUserProfile, logout, isLoginOpen, setIsLoginOpen }}>
      {isAuthInitializing ? <AuthLoadingScreen /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};
